/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

type DateTimeParts = {
    wochentag: string;
    tag: string;
    monat: string;
    jahr: string;
    stunde: string;
    minute: string;
    uhr: string;
}

export function formatDateTimeToParts(locale: string, dateTime: Date): DateTimeParts {
    const dateTimeFormatter = Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        weekday: "short",
        minute: "2-digit",
        hour12: false,
    });
    const uhrFormatter = Intl.DateTimeFormat(locale, { hour: "2-digit" });
    const dateTimeParts = dateTimeFormatter.formatToParts(dateTime);
    const uhrParts = uhrFormatter.formatToParts(dateTime);
    return {
        wochentag: getTrimmedPart(dateTimeParts, "weekday"),
        tag: getTrimmedPart(dateTimeParts, "day"),
        monat: getTrimmedPart(dateTimeParts, "month"),
        jahr: getTrimmedPart(dateTimeParts, "year"),
        stunde: getTrimmedPart(dateTimeParts, "hour"),
        minute: dateTimeParts.find(p => p.type === "minute")?.value ?? "",
        uhr: uhrParts.find(p => p.type === "literal")?.value ?? "",
    };
}

function getTrimmedPart(parts: Intl.DateTimeFormatPart[], type: keyof Intl.DateTimeFormatPartTypesRegistry) {
    return (parts.find(p => p.type === type)?.value ?? "").replace(/^0*([^0$]+)/, "$1");
}

type DateTimePartsComparison = "no-range" | "same-day" | "same-month" | "same-year" | null;

function compare(a: DateTimeParts, b: DateTimeParts): DateTimePartsComparison {
    if (a.jahr === b.jahr) {
        if (a.monat === b.monat) {
            if (a.tag === b.tag) {
                if (a.stunde === b.stunde && a.minute === b.minute) {
                    return "no-range";
                }

                return "same-day";
            }

            return "same-month";
        }

        return "same-year";
    }

    return null;
}

function toTimeString(a: DateTimeParts, options?: { ohneUhr?: boolean, ohneMinuten?: boolean }) {
    let res = `${a.stunde}:${a.minute}`
    if (options?.ohneMinuten) {
        res = res.replace(":00", "");
    }

    if (!options?.ohneUhr) {
        res += `${a.uhr}`;
    }

    return res;
}

function toDateString(a: DateTimeParts, options?: { ohneJahr: boolean }) {
    let res = `${a.wochentag}, ${a.tag}. ${a.monat}`;
    if (!options?.ohneJahr) {
        res += ` ${a.jahr}`;
    }

    return res;
}

function formatDateTimePartsToStrings(a: DateTimeParts, options?: { ohneMinuten?: boolean }) {
    return {
        zeitOhneUhr: toTimeString(a, { ohneUhr: true, ohneMinuten: options?.ohneMinuten }),
        zeit: toTimeString(a, options),
        datumOhneJahr: toDateString(a, { ohneJahr: true }),
        datum: toDateString(a),
    };
}

export default function formatDateTimeRange(dateTimeRange: { from: string, to: string }, locale: string): string {
    const fromDate = new Date(dateTimeRange.from);
    const toDate = new Date(dateTimeRange.to);
    if (isNaN(fromDate.valueOf()) || isNaN(toDate.valueOf())) {
        return `${dateTimeRange.from} - ${dateTimeRange.to}`;
    }

    const fromParts = formatDateTimeToParts(locale, fromDate);
    const toParts = formatDateTimeToParts(locale, toDate);
    const ohneMinuten = fromParts.minute === "00" && toParts.minute === "00";
    const fromStrings = formatDateTimePartsToStrings(fromParts, { ohneMinuten });
    const toStrings = formatDateTimePartsToStrings(toParts, { ohneMinuten });
    switch (compare(fromParts, toParts)) {
        case "no-range":
            return `${fromStrings.datum}, ${fromStrings.zeit}`;
        case "same-day":
            return `${toStrings.datum}, ${fromStrings.zeitOhneUhr} - ${toStrings.zeit}`;
        case "same-month":
            return `${fromStrings.datumOhneJahr}, ${fromStrings.zeit} - ${toStrings.datum}, ${toStrings.zeit}`;
        case "same-year":
            return `${fromStrings.datumOhneJahr}, ${fromStrings.zeit} - ${toStrings.datum}, ${toStrings.zeit}`;
        default:
            return `${fromStrings.datum}, ${fromStrings.zeit} - ${toStrings.datum}, ${toStrings.zeit} `;
    }
}